import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import type {
  ActivationSlip,
  AdminCompanySettings,
  AdminOverview,
  AdminUser,
  AdminUsersPage,
  ExternallyOwned,
  MesConfig,
  MesConfigInput,
  MesInbound,
  MesInboundKey,
  Paginated,
  Shift,
  UserBulkAction,
  UserBulkResult,
  UserRole,
  UserStatus,
  WorkCenter,
} from '@/types/api'

/*
 * Admin-only server calls, kept separate from lib/queries.ts.
 *
 * Everything here sits behind the `admin` middleware on the server. The split
 * keeps the main query module readable and makes it obvious at a glance which
 * calls a normal user will never make.
 */

export const adminKeys = {
  overview: ['admin', 'overview'] as const,
  users: (filters: unknown) => ['admin', 'users', filters] as const,
  plants: ['admin', 'plants'] as const,
  shifts: ['admin', 'shifts'] as const,
  workCenters: ['admin', 'work-centers'] as const,
  templates: ['admin', 'templates'] as const,
  activity: (filters: unknown) => ['admin', 'activity', filters] as const,
  activityLogNames: ['admin', 'activity', 'log-names'] as const,
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Plant extends ExternallyOwned {
  id: string
  code: string
  name: string
  address: string | null
  isActive: boolean
  sortOrder: number
  workCenterCount: number
  userCount: number
}

export interface AdminTemplateItem {
  id: string
  criterion: string
  guidance: string | null
  sortOrder: number
  isActive: boolean
}

export interface AdminTemplateCategory {
  id: string
  name: string
  description: string | null
  sortOrder: number
  items: AdminTemplateItem[]
}

export interface AdminTemplate {
  id: string
  name: string
  /**
   * The machine type this checklist covers.
   *
   * Null when the checklist was written for a single machine — see
   * `workCenterId`. Exactly one of the two is set, and the server enforces it:
   * a machine's checklist deliberately claims no type, so that nothing asking
   * "does this type have a checklist" answers yes on the strength of a sheet
   * covering one machine.
   */
  workCenterType: string | null
  /**
   * The machine this checklist was written for, if it is not a type's.
   *
   * The card renders differently for one — it names the machine, offers giving
   * it back to its type, and does not pretend to cover anything else.
   */
  workCenterId: string | null
  version: number
  scaleMax: number
  isActive: boolean
  notes: string | null
  evaluationCount: number
  /**
   * The day of the last submitted walk, `YYYY-MM-DD`; null if never walked.
   *
   * What tells a checklist that was used and then retired from one that was
   * switched off before anybody used it.
   */
  lastEvaluatedOn: string | null
  itemCount: number
  /** True once a submitted evaluation exists — criteria become read-only. */
  isLocked: boolean
  /**
   * The machines this checklist covers.
   *
   * Joined on `type` rather than a key, so it is the server's answer to a
   * question the client cannot work out for itself. Empty means the type is no
   * longer in use by any active work centre.
   *
   * A machine that has been given a checklist of its own is **subtracted** from
   * its type's list here, because the type's sheet no longer reaches it — and
   * the list under the card is read as "what this edit changes".
   */
  workCenters: { id: string; code: string; name: string }[]
  categories: AdminTemplateCategory[]
}

export interface ActivityEntry {
  id: number
  logName: string | null
  event: string | null
  description: string
  /** What happened, in Serbian — "Kreirano", "Izmijenjeno", … */
  eventLabel: string
  subjectType: string | null
  subjectId: string | null
  /** The reference number or name of the thing acted on, if it still exists. */
  subjectTitle: string | null
  /** Whether subjectId can be linked to /prijave/:id. */
  isSubmission: boolean
  /**
   * Where the subject can be opened — a report, a 5S walk, a person's page —
   * or null for one with no page of its own. Absent from an older server.
   */
  subjectPath?: string | null
  causer: { id: string; name: string; employeeCode: string } | null
  changes: { field: string; from: string | null; to: string | null }[]
  /**
   * The support person who was actually at the keyboard, if any.
   *
   * `causer` above stays the customer's own user — that is who the change
   * appears as everywhere else, and a log that disagreed with the application
   * would be worse than one that was merely incomplete. This is the correction
   * beside it, and it is deliberately on the customer's own screen.
   */
  impersonatedBy: string | null
  at: string | null
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

/**
 * The overview's counts, in one request.
 *
 * Always refetched on arrival: it is a page somebody opens to find out what
 * changed, and thirty seconds of cache would show them the count they just
 * fixed on the screen they came back from.
 */
export function useAdminOverview() {
  return useQuery({
    queryKey: adminKeys.overview,
    queryFn: async () => (await api.get<{ data: AdminOverview }>('/admin/overview')).data.data,
    refetchOnMount: 'always',
  })
}

export interface UserFilters {
  search?: string
  /** One role, or several — the server takes either. */
  role?: string | string[]
  /** People who see any of these plants, everybody with every plant included. */
  plantId?: string[]
  /** One status tab. `bez-pogona` is refused to anybody but the glavni administrator. */
  status?: UserStatus
  sort?: 'name' | 'lastLogin' | 'role' | 'createdAt'
  dir?: 'asc' | 'desc'
  isActive?: boolean
  /**
   * Who has never been able to sign in.
   *
   * The activation sheet names the people it is about to issue codes for before
   * it issues them, and counting them on the client meant paging the whole
   * roster — which gets the total wrong past the first hundred, and the total is
   * the number an administrator decides on.
   */
  mustSetPassword?: boolean
  /**
   * `none`: only the active people with no plant at all — the ones who see
   * nothing until somebody gives them one. The glavni administrator only; the
   * server answers anybody else with a 403.
   */
  access?: 'none'
  page?: number
  perPage?: number
}

export function useAdminUsers(filters: UserFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: adminKeys.users(filters),
    queryFn: async () =>
      (await api.get<AdminUsersPage>('/admin/users', { params: filters })).data,
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  })
}

/**
 * Who a bulk change is for: the ticked rows, or everybody the list's filter
 * matches — every page of it, up to the server's 500.
 */
export type UserBulkFilter = Pick<UserFilters, 'search' | 'role' | 'plantId' | 'status'>

export type UserBulkTarget = { ids: string[] } | { filter: UserBulkFilter }

/**
 * One change for many people.
 *
 * The server checks every row as it would a single edit and skips the ones a
 * single edit would refuse, so a partial result is the normal answer rather
 * than an error. For `issueActivationCodes` the codes come back here and
 * nowhere else — the same rule as the activation sheet.
 */
export function useBulkUsers() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async ({
      target,
      action,
      payload,
    }: {
      target: UserBulkTarget
      action: UserBulkAction
      payload?: { role?: UserRole; plantIds?: string[] }
    }) =>
      (
        await api.post<{ data: UserBulkResult }>('/admin/users/bulk', {
          ...target,
          action,
          payload,
        })
      ).data.data,
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/**
 * What `POST`/`PATCH admin/users` accepts.
 *
 * ## Why nothing here is required
 *
 * `name`, `employeeCode` and `role` were, and they are required to *create* a
 * user — but an update is a partial one. The server says so: on `update()`
 * they are `sometimes`, and `guardExternallyOwned` decides whether a field the
 * MES owns has been touched with `array_key_exists`, so omitting a key is how a
 * caller says "I did not edit this".
 *
 * That distinction is not expressible as one interface, and the obvious repair
 * — a `UserCreatePayload` and a `UserUpdatePayload` in a union — buys a
 * compile-time check the server already enforces at runtime, in exchange for
 * every caller having to narrow on whether an id is present. One optional shape
 * is the smaller lie: the screen that creates a user is the same dialog that
 * edits one, and it fills all three in either case.
 */
export interface UserPayload {
  name?: string
  employeeCode?: string
  email?: string | null
  phone?: string | null
  role?: UserRole
  plantId?: string | null
  /**
   * The plants an administrator grants, inside the editor's own set.
   *
   * KaproMES's grants are never in it: the server turns a plant named here
   * into an administrator's grant, which the sync can then no longer take
   * back — so a locked chip sent along with the rest would quietly change who
   * owns it. Plants outside the editor's set are left as they are whatever
   * this says.
   */
  plantIds?: string[]
  /** "Svi pogoni". The glavni administrator only; refused for anybody else. */
  allPlants?: boolean
  isActive?: boolean
  notifyByEmail?: boolean
  /*
   * No password field, deliberately. Creating a user issues a one-time
   * activation code that the person replaces themselves — the server ignores a
   * password sent here, and removing it from the type stops a caller believing
   * otherwise.
   */
}

export function useSaveUser() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...payload }: UserPayload & { id?: string }) => {
      /*
       * Creating returns an `activationCode` alongside the user; updating does
       * not. The whole response is returned rather than just `data`, because
       * that code appears exactly once and the caller has to show it.
       */
      const { data } = id
        ? await api.patch<{ data: AdminUser }>(`/admin/users/${id}`, payload)
        : await api.post<{ data: AdminUser; activationCode: string }>('/admin/users', payload)

      return data as { data: AdminUser; activationCode?: string }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/**
 * Issues the QR badge a worker signs in by scanning.
 *
 * `badgeUrl` comes back exactly once — it is the credential itself, and the
 * model hides the column everywhere else — so the caller must render or print
 * it before discarding the response.
 */
export function useIssueBadge() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ badgeUrl: string; message: string }>(`/admin/users/${id}/badge`)).data,
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useRevokeBadge() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete<{ message: string }>(`/admin/users/${id}/badge`)).data,
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/**
 * Issues a fresh activation code for someone locked out.
 *
 * Takes no password — recovering access must not mean an administrator
 * choosing the credential that ends up on the account. The code comes back
 * once, to be read out; the user replaces it on their next sign-in.
 */
export function useResetPassword() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ activationCode: string; message: string }>(
        `/admin/users/${id}/password`,
      )).data,
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/**
 * A fresh activation code for everybody who has never signed in.
 *
 * The bulk counterpart of `useResetPassword`, and the reason it exists rather
 * than a loop over that one: the nightly MES sync creates accounts at 03:20 and
 * returns their codes to a process nobody is watching. By morning a shift of
 * people have active accounts and a credential that was never written down, and
 * the codes are hashed so there is nothing to look up.
 *
 * Takes no arguments on purpose. The set is whoever is waiting in the
 * administrator's own company and the server decides it — nobody can name an
 * account and be handed its code.
 *
 * The codes come back exactly once. Print before navigating away; the way back
 * is another run of this, not a lookup.
 */
export function useIssueActivationCodes() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async () =>
      (
        await api.post<{ activations: ActivationSlip[]; message: string }>(
          '/admin/users/activation-codes',
        )
      ).data,
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/admin/users/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

// ---------------------------------------------------------------------------
// Plants and work centers
// ---------------------------------------------------------------------------

/**
 * The shifts this customer works, including the retired ones.
 *
 * Unlike `useShifts()` next door, which returns only what a walk may still be
 * filed under. An administrator has to see a deactivated shift precisely
 * because it is still attached to last year's walks.
 */
export function useAdminShifts() {
  return useQuery({
    queryKey: adminKeys.shifts,
    queryFn: async () => (await api.get<{ data: Shift[] }>('/admin/shifts')).data.data,
  })
}

export interface ShiftPayload {
  name: string
  /** `HH:MM`, or null to say this shift is a team rather than a time slot. */
  startsAt?: string | null
  endsAt?: string | null
  sortOrder?: number
  isActive?: boolean
}

/**
 * Create and edit shifts.
 *
 * Invalidates the *walk* form's shift list too, not only the admin one: they
 * are separate cache entries reading the same rows, so renaming a shift here
 * would otherwise leave the picker offering the old name until something else
 * happened to refetch it.
 */
export function useSaveShift() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...payload }: ShiftPayload & { id?: string }) => {
      const { data } = id
        ? await api.patch<{ data: Shift }>(`/admin/shifts/${id}`, payload)
        : await api.post<{ data: Shift }>('/admin/shifts', payload)

      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.shifts })
      qc.invalidateQueries({ queryKey: ['shifts'] })
      qc.invalidateQueries({ queryKey: ['evaluations'] })
    },
  })
}

/**
 * Remove a shift, or retire it.
 *
 * Which of the two happened is the server's decision — a shift that appears in
 * the archive is deactivated so the charts keep their labels — so the reply is
 * typed to carry the sentence explaining it rather than assuming a deletion.
 */
export function useDeleteShift() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete<{ message: string; data?: Shift }>(`/admin/shifts/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.shifts })
      qc.invalidateQueries({ queryKey: ['shifts'] })
    },
  })
}

export function useAdminPlants() {
  return useQuery({
    queryKey: adminKeys.plants,
    queryFn: async () => (await api.get<{ data: Plant[] }>('/admin/plants')).data.data,
  })
}

export interface PlantPayload {
  code: string
  name: string
  address?: string | null
  sortOrder?: number
  isActive?: boolean
}

/**
 * Create and edit plants.
 *
 * Invalidates work centres as well as plants: every work centre carries its
 * plant's name in the list, so renaming one leaves the other screen showing the
 * old name until something else happens to refetch it.
 */
export function useSavePlant() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...payload }: PlantPayload & { id?: string }) => {
      const { data } = id
        ? await api.patch<{ data: Plant }>(`/admin/plants/${id}`, payload)
        : await api.post<{ data: Plant }>('/admin/plants', payload)

      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.plants })
      qc.invalidateQueries({ queryKey: adminKeys.workCenters })
    },
  })
}

export function useDeletePlant() {
  const qc = useQueryClient()

  return useMutation({
    // Typed for the message and counts, because deleting a plant now takes
    // its work centres and their history with it and the caller says so.
    mutationFn: async (id: string) =>
      (
        await api.delete<{
          message: string
          counts: { workCenters: number; submissions: number; evaluations: number }
        }>(`/admin/plants/${id}`)
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.plants })
      qc.invalidateQueries({ queryKey: adminKeys.workCenters })
    },
  })
}

export function useAdminWorkCenters() {
  return useQuery({
    queryKey: adminKeys.workCenters,
    queryFn: async () =>
      (await api.get<{ data: WorkCenter[] }>('/admin/work-centers')).data.data,
  })
}

export interface WorkCenterPayload {
  /*
   * Optional because a field the MES owns is disabled on a synced centre, and
   * a disabled field is left out of the request rather than sent as "null" —
   * see `submitted` in WorkCenters.tsx. Creating a centre always sends all
   * four; the server's rules require them there.
   */
  plantId?: string
  code?: string
  name?: string
  type?: string
  description?: string | null
  sortOrder?: number
  isActive?: boolean
}

export function useSaveWorkCenter() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...payload }: WorkCenterPayload & { id?: string }) => {
      const { data } = id
        ? await api.patch(`/admin/work-centers/${id}`, payload)
        : await api.post('/admin/work-centers', payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'work-centers'] })
      // The public list feeds every submission form, so it has to refresh too.
      qc.invalidateQueries({ queryKey: ['work-centers'] })
    },
  })
}

export function useDeleteWorkCenter() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete<{ message: string; deactivated: boolean }>(`/admin/work-centers/${id}`))
        .data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'work-centers'] })
      qc.invalidateQueries({ queryKey: ['work-centers'] })
      /*
       * Plants too, and not as a precaution.
       *
       * Each plant card carries `workCenterCount`, so adding or removing a
       * centre changes a number on a page this mutation never touched. Without
       * this the plants screen kept showing the old count until a hard reload —
       * and while the delete button was gated on that count, a plant whose last
       * centre had just been removed still refused to delete.
       */
      qc.invalidateQueries({ queryKey: adminKeys.plants })

    },
  })
}

// ---------------------------------------------------------------------------
// 5S templates
// ---------------------------------------------------------------------------

/** A machine type with no checklist, and how many machines it strands. */
export interface TemplateGap {
  workCenterType: string
  machines: number
}

export function useAdminTemplates() {
  return useQuery({
    queryKey: adminKeys.templates,
    queryFn: async () =>
      (await api.get<{ data: AdminTemplate[] }>('/admin/five-s-templates')).data.data,
  })
}

/**
 * Any template mutation invalidates the whole template tree — it is small.
 *
 * TResult is a separate parameter so the caller keeps the real return type;
 * without it every mutation resolves to `unknown` and callers cannot read the
 * server's message back out.
 */
function useTemplateMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'templates'] })
      qc.invalidateQueries({ queryKey: ['five-s-template'] })
    },
  })
}

/**
 * Machine types in use that have no active 5S checklist.
 *
 * The gap the MES import made visible: Livprom run twelve machine types and had
 * checklists for five, so fifteen of thirty-two machines could not be evaluated
 * at all — and the only way to find that out was to stand at one.
 */
export function useTemplateGaps() {
  return useQuery({
    queryKey: [...adminKeys.templates, 'gaps'],
    queryFn: async () =>
      (await api.get<{ data: TemplateGap[] }>('/admin/five-s-templates-gaps')).data.data,
  })
}

/**
 * Apply one checklist to several machine types at once.
 *
 * Not the same as a new version: that supersedes the checklist for its own type,
 * this starts one for types that had none and leaves the original alone.
 */
export function useCopyTemplateToTypes() {
  return useTemplateMutation(
    async ({ templateId, types }: { templateId: string; types: string[] }) => {
      const { data } = await api.post<{
        created: { id: string; workCenterType: string }[]
        skipped: string[]
        message: string
      }>(`/admin/five-s-templates/${templateId}/copy-to-types`, { workCenterTypes: types })

      return data
    },
  )
}

/**
 * A brand-new 5S checklist, for a machine type that has none.
 *
 * Distinct from both siblings: `useCreateTemplateVersion` supersedes an
 * existing checklist for the same type, `useCopyTemplateToTypes` clones one
 * onto types that have none, and this starts an empty one. Livprom's five were
 * seeded; anything they run that we never anticipated has to be creatable, or
 * the only way to add a checklist is to copy one that is wrong for it.
 */
export function useCreateTemplate() {
  return useTemplateMutation(
    async (input: { name: string; workCenterType: string; scaleMax: number; notes?: string }) => {
      const { data } = await api.post<{ data: AdminTemplate }>('/admin/five-s-templates', input)

      return data
    },
  )
}

export function useCreateTemplateVersion() {
  return useTemplateMutation(async (templateId: string) => {
    const { data } = await api.post(`/admin/five-s-templates/${templateId}/versions`)
    return data
  })
}

/**
 * The checklist's own header: its name, its scale and its note.
 *
 * Separate from every other mutation here because it is the only one that edits
 * a checklist rather than its contents — and until it existed there was no way
 * at all to change a scale from the interface. The customer asked to move
 * Montaža from 1–3 to 1–5 and could not find how, which is what that means in
 * practice: the server has accepted `scaleMax` on this endpoint all along.
 *
 * The server refuses a new scale on a checklist that has been used, because
 * every stored percentage was computed against the old one. The screen says so
 * at the field rather than waiting for the rejection.
 */
export function useSaveTemplate() {
  return useTemplateMutation(
    async ({
      id,
      ...payload
    }: {
      id: string
      name?: string
      scaleMax?: number
      notes?: string | null
    }) => {
      const { data } = await api.patch<{ data: AdminTemplate }>(
        `/admin/five-s-templates/${id}`,
        payload,
      )

      return data.data
    },
  )
}

/**
 * Switch a checklist on or off.
 *
 * Its own hook rather than a field of `useSaveTemplate`, because it is not an
 * edit somebody saves with the rest of a form — it changes which sheet the
 * machines are scored against the moment it lands.
 *
 * Switching one on takes over from the checklist in use for the same type (or
 * the same machine); the server says which one in `replaced`, and the screen
 * has already said so in its confirmation.
 */
export function useSetTemplateActive() {
  return useTemplateMutation(async ({ id, isActive }: { id: string; isActive: boolean }) => {
    const { data } = await api.patch<{
      data: AdminTemplate
      replaced: { id: string; name: string; version: number }[]
    }>(`/admin/five-s-templates/${id}`, { isActive })

    return data
  })
}

/**
 * Carve one machine out of its type, starting from this checklist.
 *
 * Copies rather than re-points, which is the server's decision and the right
 * one: re-pointing a type's checklist at one machine would take it away from
 * every other machine of that type in the same request.
 */
export function useAttachTemplateToWorkCenter() {
  return useTemplateMutation(
    async ({ templateId, workCenterId }: { templateId: string; workCenterId: string }) => {
      const { data } = await api.post<{ data: AdminTemplate; message: string }>(
        `/admin/five-s-templates/${templateId}/work-center`,
        { workCenterId },
      )

      return data
    },
  )
}

/**
 * Give a machine back to its type's checklist.
 *
 * `hasTemplate` is the half worth reading: false means the machine's type has
 * no checklist either, so the machine cannot be evaluated at all now. That is
 * allowed — refusing would trap a customer whose machine's type never had a
 * sheet — and the caller has to say it rather than report a plain success.
 */
export function useDetachTemplateFromWorkCenter() {
  return useTemplateMutation(async (templateId: string) => {
    const { data } = await api.delete<{
      message: string
      deactivated: boolean
      hasTemplate: boolean
    }>(`/admin/five-s-templates/${templateId}/work-center`)

    return data
  })
}

export function useSaveCriterion() {
  return useTemplateMutation(
    async ({
      itemId,
      categoryId,
      criterion,
      guidance,
    }: {
      itemId?: string
      categoryId?: string
      criterion: string
      guidance?: string | null
    }) => {
      const { data } = itemId
        ? await api.patch(`/admin/five-s-items/${itemId}`, { criterion, guidance })
        : await api.post(`/admin/five-s-categories/${categoryId}/items`, { criterion, guidance })
      return data
    },
  )
}

export function useDeleteCriterion() {
  return useTemplateMutation(async (itemId: string) => {
    const { data } = await api.delete<{ message: string; deactivated: boolean }>(
      `/admin/five-s-items/${itemId}`,
    )
    return data
  })
}

export function useSaveCategory() {
  return useTemplateMutation(
    async ({ templateId, name }: { templateId: string; name: string }) => {
      const { data } = await api.post(`/admin/five-s-templates/${templateId}/categories`, { name })
      return data
    },
  )
}

/**
 * Remove a category, and the criteria inside it.
 *
 * Unlike `useDeleteCriterion` there is no `deactivated` to read back: a
 * criterion that has been scored is deactivated and kept, because an old answer
 * still points at it, whereas a category holds no answers of its own. So the
 * server either removes it or refuses — and it refuses exactly when something
 * has been scored under it, whether or not the checklist is locked.
 *
 * The caller therefore has to show the refusal rather than treat a rejection as
 * a failure of the request: the message names the reason and the way through.
 */
export function useDeleteCategory() {
  return useTemplateMutation(async (categoryId: string) => {
    const { data } = await api.delete<{ message: string }>(
      `/admin/five-s-categories/${categoryId}`,
    )

    return data
  })
}

// ---------------------------------------------------------------------------
// Activity log
// ---------------------------------------------------------------------------

export interface ActivityFilters {
  logName?: string
  causerId?: string
  /** The subject's *label*, never its class name — see the controller. */
  subjectType?: string
  event?: string
  from?: string
  to?: string
  search?: string
  page?: number
  perPage?: number
}

/** Everything the filter bar needs, in one request. */
export interface ActivityFilterOptions {
  logNames: string[]
  subjectTypes: string[]
  events: { value: string; label: string }[]
  causers: { id: string; name: string; employeeCode: string }[]
}

export function useActivityLog(filters: ActivityFilters) {
  return useQuery({
    queryKey: adminKeys.activity(filters),
    queryFn: async () =>
      (await api.get<Paginated<ActivityEntry>>('/admin/activity', { params: filters })).data,
    placeholderData: (previous) => previous,
  })
}

/**
 * The dropdown contents, fetched once and cached.
 *
 * One request rather than four: the bar is useless until all of them have
 * arrived, so separate calls would only add chances for one to fail and leave
 * it half-populated.
 */
export function useActivityFilterOptions() {
  return useQuery({
    queryKey: adminKeys.activityLogNames,
    queryFn: async () =>
      (await api.get<ActivityFilterOptions>('/admin/activity/filters')).data,
    staleTime: 10 * 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// The company's own MES integration
// ---------------------------------------------------------------------------

/**
 * This company's MES settings.
 *
 * No company id anywhere: the server reads it from the signed-in
 * administrator, which is the whole security difference from the owner
 * console's version of these calls. As there, the response never contains the
 * token or the shared secret — only whether one is stored and its last four
 * characters.
 */
/**
 * The company's own settings. Currency today, and named for the category so the
 * next one does not need a second hook.
 */
export function useAdminCompanySettings() {
  return useQuery({
    queryKey: ['admin', 'company'],
    queryFn: async () =>
      (await api.get<{ data: AdminCompanySettings }>('/admin/company')).data.data,
  })
}

export function useSaveAdminCurrency() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (currency: string) => {
      const { data } = await api.patch<{ data: AdminCompanySettings }>('/admin/company', {
        currency,
      })

      return data.data
    },
    /*
     * Everything that shows an amount, not just this screen.
     *
     * The currency is carried on the session — see lib/currency.ts — and every
     * `formatMoney` in the application reads it from there, so the caller
     * refreshes the session as well. Invalidating the query cache alone would
     * update this one form and leave every figure elsewhere in the old symbol
     * until a reload, which reads as the setting not having worked.
     */
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'company'] }),
  })
}

/**
 * Hand the glavni administrator role to another active administrator.
 *
 * In compat mode any administrator may call it, and then it is the choosing
 * rather than a hand-over. Either way the session changes underneath the
 * caller — they may stop being the glavni administrator — so the caller
 * refreshes it; the People list is refetched here because its badges and
 * `editable` flags follow the role.
 */
export function useTransferSuperAdmin() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string) =>
      (
        await api.post<{ data: { superAdminId: string } }>('/admin/company/super-admin', {
          userId,
        })
      ).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
  })
}

/**
 * The company's time zone. Its own call rather than a field on the currency
 * one, because the two are saved from separate cards and a save of one must
 * not resend the other. As with the currency, the caller refreshes the session
 * — the zone is carried there, and every date in the application reads it.
 */
export function useSaveAdminTimezone() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (timezone: string) => {
      const { data } = await api.patch<{ data: AdminCompanySettings }>('/admin/company', {
        timezone,
      })

      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'company'] }),
  })
}

/**
 * The company's 5S target, from a card of its own like the zone. The caller
 * refreshes the session too: the target is carried there, and every score
 * colour in the application reads it — see lib/fiveSTarget.ts.
 */
export function useSaveAdminFiveSTarget() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (fiveSTarget: number) => {
      const { data } = await api.patch<{ data: AdminCompanySettings }>('/admin/company', {
        fiveSTarget,
      })

      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'company'] }),
  })
}

export function useAdminMes() {
  return useQuery({
    queryKey: ['admin', 'mes'],
    queryFn: async () => (await api.get<{ data: MesConfig }>('/admin/mes')).data.data,
  })
}

export function useSaveAdminMes() {
  const qc = useQueryClient()

  return useMutation({
    /*
     * Secrets follow the keep-unless-told rule the server enforces:
     * omitted or '' keeps, the literal 'null' clears, anything else replaces.
     * The form cannot round-trip a secret it never received.
     */
    mutationFn: async (input: MesConfigInput) => {
      const { data } = await api.put<{ data: MesConfig; message: string }>('/admin/mes', input)

      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'mes'] }),
  })
}

export function useTestAdminMes() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{
        ok: boolean
        message: string
        plants: { id: string; name: string | null }[]
      }>('/admin/mes/test')

      return data
    },
  })
}

/**
 * What a sync run actually found.
 *
 * `problems` is the half worth reading. It carries the machines whose type has
 * no 5S checklist and the plants the MES answered oddly for — a sync can import
 * thirty-two work centres and leave fifteen of them unevaluable, and the row
 * counts say nothing about that. It was typed `unknown` and thrown away, so
 * every run reported "sinhronizacija završena" and stopped there.
 */
export interface MesSyncResult {
  message: string
  counts: {
    plants: number
    workCenters: number
    workers: number
    deactivated: number
    /** Rows entered by hand that the MES claimed rather than duplicating. */
    adopted: number
  }
  /** Empty means the run was clean, which is different from absent. */
  problems: string[]
  /**
   * The activation codes this run issued, for the accounts it created.
   *
   * In plaintext and exactly once — the server hashes them on the row, so this
   * response is the only readable copy that will ever exist. Empty on every run
   * that creates nobody, which is almost all of them.
   *
   * Deliberately not folded into `problems`: that list is rendered as prose and
   * copied into support tickets, and a credential has no business there.
   */
  activations: ActivationSlip[]
}

export function useSyncAdminMes() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<MesSyncResult>('/admin/mes/sync')

      return data
    },
    onSuccess: () => {
      // The sync rewrites plants, work centres and people.
      qc.invalidateQueries({ queryKey: ['admin'] })
    },
    // A failed run is recorded too, and the history on the page should show
    // the failure the toast has just reported.
    onError: () => qc.invalidateQueries({ queryKey: ['admin', 'mes'] }),
  })
}

/**
 * What KaproMES has sent us: the receiver's address, our key's state, and the
 * latest deliveries.
 *
 * Under `['admin', 'mes']`, so saving the settings or running a sync — both of
 * which invalidate that prefix — refreshes this too. Polled while the page is
 * open: the reason somebody is looking at it is usually that KaproMES is being
 * set up on the other end of a phone call, and "did it arrive" should not need
 * a reload.
 */
export function useAdminMesInbound() {
  return useQuery({
    queryKey: ['admin', 'mes', 'inbound'],
    queryFn: async () => (await api.get<{ data: MesInbound }>('/admin/mes/inbound')).data.data,
    refetchInterval: 30_000,
  })
}

/**
 * Issue a new inbound key, replacing the old one at once.
 *
 * The response carries the key in plaintext, and it is the only one that ever
 * will: the server keeps a hash. So the caller copies it out into local state
 * and resets this mutation straight after, rather than leaving the credential
 * sitting in the mutation cache for as long as that keeps finished results.
 */
export function useGenerateMesInboundKey() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async () =>
      (await api.post<{ data: MesInboundKey }>('/admin/mes/inbound-key')).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'mes', 'inbound'] }),
  })
}
